import React, { useCallback, useEffect, useMemo, useState } from 'react'
import api from '../../services/api'
import '../../css/activities.css'

const fmt = (value) => value ? new Date(value).toLocaleString('it-IT', { dateStyle: 'medium', timeStyle: 'short' }) : '—'
const number = (value) => new Intl.NumberFormat('it-IT').format(value ?? 0)

const actionMeta = {
  create: { label: 'Creazione', icon: 'fa-plus', tone: 'green' },
  update: { label: 'Modifica', icon: 'fa-pen', tone: 'blue' },
  delete: { label: 'Eliminazione', icon: 'fa-trash', tone: 'red' },
  read: { label: 'Consultazione', icon: 'fa-eye', tone: 'slate' },
  auth: { label: 'Accesso', icon: 'fa-key', tone: 'violet' },
  operation: { label: 'Operazione', icon: 'fa-bolt', tone: 'amber' },
}

function Kpi({ icon, label, value, detail, tone }) {
  return (
    <article className={`ac-kpi ac-kpi--${tone}`}>
      <i className={`fas ${icon}`} />
      <div><span>{label}</span><strong>{number(value)}</strong><small>{detail}</small></div>
    </article>
  )
}

function ActivityDetail({ activity, onClose }) {
  if (!activity) return null
  const meta = actionMeta[activity.actionType] || actionMeta.operation
  return (
    <div className="ac-modal-backdrop" onClick={onClose}>
      <section className="ac-modal" onClick={(event) => event.stopPropagation()}>
        <button className="ac-modal__close" onClick={onClose} aria-label="Chiudi">×</button>
        <div className={`ac-modal__icon ac-tone--${meta.tone}`}><i className={`fas ${meta.icon}`} /></div>
        <span className="ac-overline">Dettaglio attività</span>
        <h2>{activity.description}</h2>
        {activity.resourceLabel && <p className="ac-modal__target">{activity.resourceLabel}</p>}
        <div className="ac-detail-grid">
          <div><span>Utente</span><strong>{activity.actorLabel}</strong></div>
          <div><span>Data e ora</span><strong>{fmt(activity.occurredAt)}</strong></div>
          <div><span>Area</span><strong>{activity.domain}</strong></div>
          <div><span>Esito</span><strong className={activity.success ? 'ac-ok' : 'ac-ko'}>{activity.success ? 'Riuscita' : `Fallita (${activity.status})`}</strong></div>
          <div><span>Risorsa</span><strong>{activity.resourceId ? `#${activity.resourceId}` : '—'}</strong></div>
          <div><span>Durata</span><strong>{activity.durationMs} ms</strong></div>
          <div><span>Indirizzo IP</span><strong>{activity.ip || '—'}</strong></div>
          <div><span>Request ID</span><strong className="ac-mono">{activity.requestId}</strong></div>
        </div>
        <div className="ac-technical"><code>{activity.method}</code><span>{activity.path}</span></div>
      </section>
    </div>
  )
}

export default function ActivitiesPage() {
  const [payload, setPayload] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [selected, setSelected] = useState(null)
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [filters, setFilters] = useState({
    days: '14', scope: 'programming', mode: 'changes', actor_id: '', result: 'all', search: '', page: 1,
  })

  const fetchActivities = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true)
    setError('')
    try {
      const { data } = await api.get('/activities', { params: { ...filters, limit: 30 } })
      setPayload(data)
    } catch (requestError) {
      setError(requestError?.response?.data?.error || 'Impossibile caricare le attività')
    } finally {
      if (!quiet) setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    const timeout = window.setTimeout(() => fetchActivities(), filters.search ? 280 : 0)
    return () => window.clearTimeout(timeout)
  }, [fetchActivities, filters.search])

  useEffect(() => {
    if (!autoRefresh) return undefined
    const interval = window.setInterval(() => fetchActivities({ quiet: true }), 60_000)
    return () => window.clearInterval(interval)
  }, [autoRefresh, fetchActivities])

  const updateFilter = (key, value) => setFilters((current) => ({ ...current, [key]: value, page: key === 'page' ? value : 1 }))
  const summary = payload?.summary || {}
  const maxActor = Math.max(...(payload?.byActor || []).map((item) => item.total), 1)
  const contextLabel = useMemo(() => {
    const scope = { programming: 'Programmazioni', content: 'Contenuti', users: 'Utenti', instagram: 'Instagram', operations: 'Servizi', all: 'Tutta la dashboard' }[filters.scope]
    const mode = { changes: 'modifiche', reads: 'consultazioni', auth: 'accessi', all: 'tutte le azioni' }[filters.mode]
    return `${scope} · ${mode} · ultimi ${filters.days} giorni`
  }, [filters.days, filters.mode, filters.scope])

  return (
    <main className="ac-shell">
      <header className="ac-hero">
        <div>
          <span className="ac-overline"><i className="fas fa-shield-halved" /> Audit dashboard</span>
          <h1>Activities</h1>
          <p>Una traccia leggibile e verificabile di chi ha consultato o modificato la dashboard.</p>
        </div>
        <div className="ac-hero__actions">
          <span><i className="fas fa-clock" /> {payload?.generatedAt ? `Aggiornato ${fmt(payload.generatedAt)}` : 'In aggiornamento'}</span>
          <label><input type="checkbox" checked={autoRefresh} onChange={(event) => setAutoRefresh(event.target.checked)} /> Live ogni 60s</label>
          <button onClick={() => fetchActivities()} disabled={loading}><i className={`fas fa-rotate ${loading ? 'fa-spin' : ''}`} /> Aggiorna</button>
        </div>
      </header>

      <section className="ac-filter-panel">
        <div className="ac-search"><i className="fas fa-magnifying-glass" /><input value={filters.search} onChange={(event) => updateFilter('search', event.target.value)} placeholder="Cerca utente, rotta o risorsa…" />{filters.search && <button onClick={() => updateFilter('search', '')}>×</button>}</div>
        <label><span>Periodo</span><select value={filters.days} onChange={(event) => updateFilter('days', event.target.value)}><option value="1">24 ore</option><option value="7">7 giorni</option><option value="14">14 giorni</option><option value="30">30 giorni</option><option value="90">90 giorni</option><option value="180">180 giorni</option></select></label>
        <label><span>Area</span><select value={filters.scope} onChange={(event) => updateFilter('scope', event.target.value)}><option value="programming">Programmazioni</option><option value="content">Contenuti</option><option value="users">Utenti e accessi</option><option value="instagram">Instagram</option><option value="operations">Altri servizi</option><option value="all">Tutta la dashboard</option></select></label>
        <label><span>Attività</span><select value={filters.mode} onChange={(event) => updateFilter('mode', event.target.value)}><option value="changes">Solo modifiche</option><option value="reads">Solo consultazioni</option><option value="auth">Accessi</option><option value="all">Tutte</option></select></label>
        <label><span>Utente</span><select value={filters.actor_id} onChange={(event) => updateFilter('actor_id', event.target.value)}><option value="">Tutti</option>{payload?.actors?.map((actor) => <option value={actor.id} key={actor.id}>{actor.label} ({actor.total})</option>)}</select></label>
        <label><span>Esito</span><select value={filters.result} onChange={(event) => updateFilter('result', event.target.value)}><option value="all">Tutti</option><option value="success">Riuscite</option><option value="failed">Fallite</option></select></label>
      </section>

      <div className="ac-context"><i className="fas fa-filter" /><strong>{contextLabel}</strong><span>{number(payload?.meta?.total)} risultati</span></div>

      <section className="ac-kpis">
        <Kpi icon="fa-list-check" label="Attività" value={summary.total} detail="nel perimetro selezionato" tone="violet" />
        <Kpi icon="fa-pen-to-square" label="Modifiche" value={summary.changes} detail="azioni che cambiano dati" tone="blue" />
        <Kpi icon="fa-users" label="Utenti attivi" value={summary.actors} detail="account distinti" tone="green" />
        <Kpi icon="fa-triangle-exclamation" label="Fallite" value={summary.failed} detail="errori o tentativi negati" tone={summary.failed ? 'red' : 'slate'} />
      </section>

      {error ? (
        <section className="ac-state ac-state--error"><i className="fas fa-circle-exclamation" /><h2>Caricamento non riuscito</h2><p>{error}</p><button onClick={() => fetchActivities()}>Riprova</button></section>
      ) : loading && !payload ? (
        <section className="ac-state"><i className="fas fa-circle-notch fa-spin" /><h2>Ricostruzione attività…</h2></section>
      ) : (
        <section className="ac-content-grid">
          <div className="ac-feed-panel">
            <div className="ac-panel-head"><div><span>Timeline</span><h2>Chi ha fatto cosa</h2></div><small>Clicca una riga per i dettagli tecnici</small></div>
            {!payload?.data?.length ? <div className="ac-empty"><i className="fas fa-inbox" /><strong>Nessuna attività trovata</strong><span>Prova ad ampliare periodo o filtri.</span></div> : (
              <div className="ac-feed">
                {payload.data.map((activity) => {
                  const meta = actionMeta[activity.actionType] || actionMeta.operation
                  return (
                    <button className="ac-event" key={activity.id} onClick={() => setSelected(activity)}>
                      <span className={`ac-event__icon ac-tone--${activity.success ? meta.tone : 'red'}`}><i className={`fas ${activity.success ? meta.icon : 'fa-circle-exclamation'}`} /></span>
                      <span className="ac-event__body"><span className="ac-event__top"><strong>{activity.actorLabel}</strong><time>{fmt(activity.occurredAt)}</time></span><span className="ac-event__description">{activity.description}</span>{activity.resourceLabel && <span className="ac-event__target"><i className="fas fa-arrow-turn-up fa-rotate-90" /> {activity.resourceLabel}</span>}</span>
                      <span className="ac-event__meta"><span>{activity.domain}</span><small>{meta.label}</small><i className="fas fa-chevron-right" /></span>
                    </button>
                  )
                })}
              </div>
            )}
            {payload?.meta && payload.meta.lastPage > 1 && <div className="ac-pagination"><button disabled={filters.page <= 1} onClick={() => updateFilter('page', filters.page - 1)}><i className="fas fa-chevron-left" /> Precedente</button><span>Pagina <strong>{filters.page}</strong> di {payload.meta.lastPage}</span><button disabled={filters.page >= payload.meta.lastPage} onClick={() => updateFilter('page', filters.page + 1)}>Successiva <i className="fas fa-chevron-right" /></button></div>}
          </div>

          <aside className="ac-ranking-panel">
            <div className="ac-panel-head"><div><span>Persone</span><h2>Utenti più attivi</h2></div></div>
            <div className="ac-ranking">
              {payload?.byActor?.map((actor, index) => (
                <button key={actor.id} onClick={() => updateFilter('actor_id', String(actor.id))} className={String(actor.id) === String(filters.actor_id) ? 'active' : ''}>
                  <span className="ac-rank">{index + 1}</span><span className="ac-avatar">{actor.label.split(/\s+/).slice(0, 2).map((part) => part[0]).join('').toUpperCase()}</span><span className="ac-rank-info"><strong>{actor.label}</strong><span><i style={{ width: `${(actor.total / maxActor) * 100}%` }} /></span><small>{actor.changes} modifiche · {actor.total} attività</small></span>
                </button>
              ))}
              {!payload?.byActor?.length && <div className="ac-empty ac-empty--small">Nessun utente nel periodo</div>}
            </div>
            <div className="ac-privacy-note"><i className="fas fa-lock" /><div><strong>Audit rispettoso dei dati</strong><span>Non vengono salvati password, token, query o contenuti inseriti nei form.</span></div></div>
          </aside>
        </section>
      )}
      <ActivityDetail activity={selected} onClose={() => setSelected(null)} />
    </main>
  )
}
